/**
 * ContactPhysicianTab — the physician workflow surface inside the
 * CRM contact detail page (Task #62 / #63, Jun 8 2026).
 *
 * Mirrors ContactPEMTab for the physician role. Carlos's principle:
 * "The Physician Diagnoses. The PEM Coordinates." This tab is where
 * the diagnosis half lives.
 *
 * Sections:
 *   - Candidacy widget — verdict picker, safety screen (6 absolute +
 *     5 relative exclusions as checkboxes), rationale, sign button
 *   - Treatment plan composer — modality category + modality dropdown
 *     filtered by category + biologic source + dose + rationale
 *   - Plan history — chronological list of signed plans
 *
 * Permission gates:
 *   physician:read   — view (PEMs see read-only version of this tab)
 *   physician:write  — draft + screen + save plan
 *   physician:sign   — sign candidacy + plan (senior physician)
 *
 * The Sign button on candidacy is disabled until all 6 absolute
 * exclusions are screened (client-side check via the canSign
 * predicate; server enforces too via isCandidacySignable).
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { PatientLinkControl } from '../../components/PatientContactLink';
import {
  Stethoscope, Shield, AlertTriangle, CheckCircle2, Clock,
  Loader2, Save, Sparkles, FileSignature, RefreshCcw,
  // Task #77: treatment proposal icons
  FileText, Plus, Trash2, Star, Send, Lock,
  // Card P77: send-to-patient panel
  Link2, Copy, Check, Ban, Mail, MessageSquare, Eye, Download,
} from "lucide-react";

/**
 * ⛔ Daniel's ruling, 10 Oct 2026: SMS, email, WhatsApp and calls are NOT
 * wired. All patient messaging goes through Twilio in a separate card. One
 * constant so the disabled control, its tooltip and the banner cannot drift
 * into saying three different things.
 */
/**
 * ⛔ PHI POSTURE FOR THE REASON FIELD — review item 6.
 *
 * The reason is free text that lands in an audit payload staff read. The
 * first placeholder ("patient signs the consent in clinic on Tuesday")
 * INVITED exactly the thing the field must not collect: it modelled a
 * sentence about this patient's care. The replacement describes the LINK, not
 * the patient, the helper text says so outright, and 200 characters is short
 * enough to be a reason and too short to be a clinical note. The server
 * enforces the same cap — a client-side maxLength is a courtesy, not a limit.
 */
/**
 * ⛔⛔ ONE PREDICATE, MIRRORING THE SERVER — review item 3.
 *
 * The server rule is `wantsResend && status === 'patient_reviewing'`
 * (routes/treatment_proposals.js). The override panel used a DIFFERENT one —
 * `sendBlocked.resend && alreadySent !== null` — which is neither the same
 * question nor reliably the same answer: `alreadySent` is a UI state derived
 * from the token list, so a resend of a proposal whose links had all expired
 * showed the box as optional and the server then refused it. Two predicates
 * for one rule is how a screen ends up disagreeing with the thing it is
 * describing.
 *
 * Exported shape kept deliberately dumb — status in, boolean out — so it can
 * be read against the server's line without translation.
 */
function reasonIsRequired(proposalStatus, willResend) {
  return !!willResend && proposalStatus === "patient_reviewing";
}

const REASON_PLACEHOLDER = "e.g. consent form to follow; link re-issued at staff request";
const REASON_HELPER = "Do not enter health details.";
const REASON_MAX_LENGTH = 200;

const CHANNEL_UNAVAILABLE_REASON =
  "Not available yet: texting and email go live with the Twilio integration.";

const VERDICT_LABEL = {
  yes: "Candidate", no: "Not a candidate", conditional: "Conditional", pending: "Pending",
};
const VERDICT_COLOR = {
  yes: "#62BB46", no: "#DC3545", conditional: "#F26522", pending: "#6C757D",
};
const STATUS_LABEL = {
  unscreened: "Unscreened", absent: "Absent", present: "Present", na: "N/A",
};
const STATUS_COLOR = {
  unscreened: "#ADB5BD", absent: "#62BB46", present: "#DC3545", na: "#6C757D",
};

// ─── Local pure helper: client-side mirror of isCandidacySignable so the
// Sign button can disable instantly without a server round-trip. Server
// still enforces the same rule on the real sign call. ────────────────────
function clientIsCandidacySignable(absoluteCodes, screenRows) {
  if (!absoluteCodes || absoluteCodes.length === 0) return false;
  const screened = new Set(
    (screenRows || [])
      .filter(r => r && r.status && r.status !== "unscreened")
      .map(r => r.exclusion_code)
  );
  return absoluteCodes.every(c => screened.has(c));
}

export default function ContactPhysicianTab({
  contactId, contactData, canSign = false, canWrite = false,
  // Card P100b item 3 — the patient link control, shared with the header.
  patientLink, onPatientLinkChanged, canCreatePatient = false, canLinkPatient = false, onOpenPatient,
  // Card P77 — the proposal permissions, which are NOT the physician ones.
  // See the block comment at ContactDetailView.js where these are derived:
  // physician:sign is a strict superset of treatment_proposal:sign, so
  // gating proposal buttons on canSign enabled them for two roles the
  // server refuses. Default false so an older caller that passes neither
  // gets a disabled button rather than a lying one.
  canReadProposal = false, canWriteProposal = false,
  canSignProposal = false, canWriteProposalDoc = false,
}) {
  const [loading, setLoading] = useState(true);
  const [reference, setReference] = useState(null); // {absolute, relative, treatment_catalog, verdicts}
  // See the render gate below: distinguishes a FIRST load from a refresh.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [candidacy, setCandidacy] = useState(null);
  const [screen, setScreen] = useState([]);          // safety_screen rows
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [okMsg, setOkMsg] = useState(null);

  // Candidacy-edit local state
  const [verdict, setVerdict] = useState("pending");
  const [conditional, setConditional] = useState("");
  const [rationale, setRationale] = useState("");

  // Treatment-plan composer state
  const [planCategory, setPlanCategory] = useState("orthopedic");
  const [planModality, setPlanModality] = useState("");
  const [planBiologic, setPlanBiologic] = useState("");
  const [planDose, setPlanDose] = useState("");
  const [planRationale, setPlanRationale] = useState("");

  // ─── Task #77: Treatment Proposal builder state ──────────────────────
  // List of proposals for this contact (loaded in reload). The expanded
  // proposal is the one currently being built / reviewed inline.
  const [proposals, setProposals] = useState([]);
  const [expandedProposalId, setExpandedProposalId] = useState(null);
  const [proposalDetail, setProposalDetail] = useState(null);  // { proposal, options, addons, audit_log }

  // Builder draft for adding a new option to the expanded proposal
  const [optDraft, setOptDraft] = useState({
    modalityCategory: "orthopedic", modality: "", biologicSource: "",
    doseDescription: "", priceDollars: "",
  });

  // Task #100 — Phase 4f: physician counter-sign state (per-proposal).
  // Each proposal in 'patient_signed' status can show a counter-sign card.
  // Form state: typed name + ESIGN consent ack + in-flight flag.
  const [counterSignDraft, setCounterSignDraft] = useState({
    typedName: "", esignConsent: false, signing: false,
  });

  // ─── Card P77 (item 13): "Send proposal to patient" ──────────────────
  // sendResult holds the ONE-TIME cleartext link returned by the send call.
  // It is deliberately NOT persisted and NOT re-fetchable: the DB stores
  // only SHA-256(token), so once this state is cleared the link is gone and
  // the operator must issue a new one. The panel says so.
  const [sendResult, setSendResult] = useState(null);
  const [sendBusy, setSendBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Tokens already issued for the expanded proposal — this is what survives a
  // reload and carries the "sent by / when" record plus the Revoke control.
  const [proposalTokens, setProposalTokens] = useState([]);
  const [proposalDocs, setProposalDocs] = useState([]);
  const [docResults, setDocResults] = useState(null);
  // Card P77 review item 2: the server refused the send because required
  // documents are missing. Holds the AUTHORITATIVE missing list the server
  // computed, which is what the override must acknowledge back verbatim.
  const [sendBlocked, setSendBlocked] = useState(null);
  // Review item 1: the server refused because a live link already exists.
  // Holds its details so the panel can say WHO sent it and WHEN, instead of
  // offering a bare Send button that mints a second live credential.
  const [alreadySent, setAlreadySent] = useState(null);
  // The server refused a resend for want of a reason (patient_reviewing).
  const [reasonRequired, setReasonRequired] = useState(false);
  /**
   * ONE reason field, shared by the override and the resend.
   *
   * ⚠ Two boxes would mean two states to clear, two placeholders to keep in
   * step, and an operator facing both at once (resending a proposal whose
   * consent form is missing) typing the same sentence twice. The server takes
   * one `reason` and records it once.
   *
   * ⛔ CAPPED AND LABELLED — review item 6. It lands in an audit payload staff
   * read, so it must not become a place to paste clinical detail. 200 chars,
   * enforced on the server too; the helper text says what not to type.
   */
  const [reasonText, setReasonText] = useState("");

  const flashOk = (msg) => { setOkMsg(msg); setTimeout(() => setOkMsg(null), 3000); };
  const flashErr = (msg) => { setErrorMsg(msg); setTimeout(() => setErrorMsg(null), 6000); };

  // ─── Initial load ──────────────────────────────────────────────────
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [ref, candResp, planResp, propResp] = await Promise.all([
        window.zignagenixAPI.invoke("physician:exclusions:list"),
        window.zignagenixAPI.invoke("physician:candidacy:get", contactId),
        window.zignagenixAPI.invoke("physician:treatment-plan:by-contact", contactId),
        window.zignagenixAPI.invoke("treatment-proposals:list-by-contact", contactId),
      ]);
      if (ref && !ref.error) setReference(ref);
      if (Array.isArray(propResp)) setProposals(propResp);
      if (candResp && !candResp.error) {
        setCandidacy(candResp.assessment || null);
        setScreen(Array.isArray(candResp.screen) ? candResp.screen : []);
        if (candResp.assessment) {
          setVerdict(candResp.assessment.verdict || "pending");
          setConditional(candResp.assessment.conditional_requirements || "");
          setRationale(candResp.assessment.rationale || "");
        }
      }
      if (Array.isArray(planResp)) setPlans(planResp);
    } catch (e) {
      flashErr(`Failed to load physician data: ${e.message}`);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [contactId]);
  useEffect(() => { if (contactId) reload(); }, [contactId, reload]);

  // ─── Derived state ─────────────────────────────────────────────────
  const absoluteCodes = useMemo(
    () => (reference?.absolute || []).map(e => e.code),
    [reference]
  );
  const screenByCode = useMemo(() => {
    const m = new Map();
    for (const r of screen) m.set(r.exclusion_code, r);
    return m;
  }, [screen]);
  const isSignable = useMemo(
    () => clientIsCandidacySignable(absoluteCodes, screen),
    [absoluteCodes, screen]
  );

  const availableModalities = useMemo(() => {
    if (!reference?.treatment_catalog) return [];
    return reference.treatment_catalog[planCategory] || [];
  }, [reference, planCategory]);

  // ─── Handlers ──────────────────────────────────────────────────────
  async function startCandidacy() {
    if (!canWrite || busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("physician:candidacy:start", { contactId });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk("Candidacy assessment started. Screen the safety exclusions below.");
      await reload();
    } catch (e) {
      flashErr(`Could not start candidacy: ${e.message}`);
    } finally { setBusy(false); }
  }

  async function setExclusionStatus(exclusionCode, status) {
    if (!canWrite || !candidacy || busy) return;
    // Optimistic update so the UI feels instant
    setScreen(prev => {
      const next = prev.filter(r => r.exclusion_code !== exclusionCode);
      const old = prev.find(r => r.exclusion_code === exclusionCode) || {};
      next.push({ ...old, exclusion_code: exclusionCode, status });
      return next;
    });
    try {
      const r = await window.zignagenixAPI.invoke("physician:safety-screen:set", {
        assessmentId: candidacy.id, exclusionCode, status,
      });
      if (r && r.error) throw new Error(r.message || r.code);
    } catch (e) {
      flashErr(`Failed to save screen status: ${e.message}`);
      await reload(); // re-sync if optimistic write was wrong
    }
  }

  async function signCandidacy() {
    if (!canSign || !candidacy || busy) return;
    if (verdict === "pending") {
      flashErr("Pick a verdict (Candidate, Not a candidate, or Conditional) before signing.");
      return;
    }
    if (!isSignable) {
      flashErr("Cannot sign until all 6 absolute exclusions are screened.");
      return;
    }
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("physician:candidacy:sign", {
        assessmentId: candidacy.id,
        verdict,
        conditionalRequirements: verdict === "conditional" ? conditional : null,
        rationale: rationale || null,
      });
      if (r && r.error) {
        throw new Error(r.message || r.code);
      }
      flashOk(`Candidacy signed as ${VERDICT_LABEL[verdict]}.`);
      await reload();
    } catch (e) {
      flashErr(`Sign failed: ${e.message}`);
    } finally { setBusy(false); }
  }

  async function savePlanDraft() {
    if (!canWrite || busy) return;
    if (!planModality) {
      flashErr("Pick a modality before saving.");
      return;
    }
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("physician:treatment-plan:save", {
        contactId,
        modalityCategory: planCategory,
        modality: planModality,
        biologicSource: planBiologic || null,
        doseDescription: planDose || null,
        rationale: planRationale || null,
        candidacyAssessmentId: candidacy?.id || null,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk("Treatment plan draft saved. Sign when ready.");
      // Clear composer
      setPlanModality(""); setPlanBiologic(""); setPlanDose(""); setPlanRationale("");
      await reload();
    } catch (e) {
      flashErr(`Save failed: ${e.message}`);
    } finally { setBusy(false); }
  }

  async function signPlan(recommendationId) {
    if (!canSign || busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("physician:treatment-plan:sign", { recommendationId });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk("Treatment plan signed.");
      await reload();
    } catch (e) {
      flashErr(`Sign failed: ${e.message}`);
    } finally { setBusy(false); }
  }

  // ─── Task #77: Treatment Proposal handlers ───────────────────────────
  // Locked behind the candidacy gate: verdict='yes' AND signed_at IS NOT NULL.
  // Server enforces the same gate via tps.canBuildProposal — these client
  // checks just keep the UI honest (disabled buttons, clear messaging).
  const candidacyAllowsProposal =
    !!(candidacy && candidacy.signed_at && candidacy.verdict === "yes");

  async function expandProposal(id) {
    setBusy(true);
    /**
     * ⛔ THE ONE-TIME LINK MUST NOT FOLLOW THE OPERATOR TO ANOTHER PROPOSAL.
     *
     * sendResult holds cleartext for ONE proposal. Expanding a different
     * proposal while it is still in state would render proposal A's live
     * magic link, with its Copy button, inside proposal B's panel — and the
     * operator would text the wrong patient's link to a patient. Cleared on
     * every expand to a different id; a re-expand of the SAME proposal (the
     * refresh at the end of a send) keeps it, which is what shows the link
     * the operator just created.
     */
    if (id !== expandedProposalId) {
      setSendResult(null);
      setDocResults(null);
      setSendBlocked(null);
      setAlreadySent(null);
      setReasonRequired(false);
      setReasonText("");
      setCopied(false);
    }
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:get", id);
      if (r && r.error) throw new Error(r.message || r.code);
      setProposalDetail(r);
      setExpandedProposalId(id);
      // Card P77: the send record + documents live on separate channels and
      // are only readable by holders of the matching permission. Loaded
      // best-effort so a PEM without :read still gets the proposal body
      // rather than an error banner over the whole card.
      await loadSendState(id);
    } catch (e) { flashErr(`Could not load proposal: ${e.message}`); }
    finally { setBusy(false); }
  }

  // ─── Card P77: tokens + documents for the Send panel ─────────────────
  async function loadSendState(proposalId) {
    if (canReadProposal) {
      try {
        const t = await window.zignagenixAPI.invoke("proposal-tokens:list", proposalId);
        const list = Array.isArray(t) ? t : [];
        setProposalTokens(list);
        /**
         * ⛔ "Sent <time>" MUST SURVIVE A RELOAD, not just appear after a
         * refused click — review item 1. Derived from the token list so that
         * reopening the proposal tomorrow shows the live link's state rather
         * than a bare Send button inviting a second bearer token.
         *
         * Active = not revoked AND not expired. An expired token is not a
         * live link, so it must not suppress Send: there is nothing left to
         * protect and the operator genuinely does need to issue a new one.
         */
        const live = list
          .filter(x => !x.revoked_at && x.expires_at && new Date(x.expires_at).getTime() > Date.now())
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAlreadySent(live.length ? {
          sentAt: live[0].created_at,
          sentByName: live[0].created_by_name || null,
          expiresAt: live[0].expires_at,
          activeTokenCount: live.length,
          message: "",
        } : null);
      } catch (_) { setProposalTokens([]); }
    } else {
      setProposalTokens([]);
    }
    try {
      const d = await window.zignagenixAPI.invoke("treatment-proposal-documents:list", proposalId);
      setProposalDocs(Array.isArray(d) ? d : []);
    } catch (_) { setProposalDocs([]); }
  }

  /**
   * Send the proposal to the patient.
   *
   * Order matters: documents FIRST, link second. The patient's very first
   * open of the magic link flips the proposal to patient_reviewing and
   * renders whatever documents exist at that moment — so minting the link
   * before the PDFs exist races the patient to their own proposal.
   *
   * ⛔⛔ AND THE LINK IS NOT MINTED AT ALL IF A REQUIRED DOCUMENT IS MISSING
   * (Card P77 review item 2). The server refuses with DOCUMENTS_MISSING and
   * the authoritative list; this surfaces that as a BLOCK, not as a note
   * underneath a link that already exists. informed_consent cannot generate
   * today, so without this gate every patient would get a live link to a
   * proposal with no consent form — and the operator would read about it
   * after the fact, with nothing to un-send.
   *
   * `acknowledgedMissing` is the override: the operator pressed "Send
   * without…". It is passed straight back to the server, which re-derives the
   * missing set itself and requires exact set equality, so this cannot wave
   * through a gap the operator never saw.
   */
  async function sendProposalToPatient(proposalId, acknowledgedMissing = null, resend = false) {
    if (!canSignProposal || sendBusy) return;
    setSendBusy(true);
    if (!acknowledgedMissing) { setDocResults(null); setSendBlocked(null); }
    if (!resend) setAlreadySent(null);
    setReasonRequired(false);
    try {
      let bundle = docResults;
      // Only build on the first attempt — the override retry must not
      // regenerate, and generate-bundle skips existing categories anyway.
      if (canWriteProposalDoc && !acknowledgedMissing) {
        bundle = await window.zignagenixAPI.invoke(
          "treatment-proposal-documents:generate-bundle", { proposalId });
        if (bundle && bundle.error) throw new Error(bundle.message || bundle.code);
        setDocResults(bundle);
      }

      /**
       * ⛔ NO CHANNEL IS EVER REQUESTED — Daniel's ruling, 10 Oct 2026.
       * Patient messaging is not wired; it goes live with the Twilio card.
       * The server REFUSES a non-empty channel list with
       * CHANNEL_NOT_AVAILABLE, so sending one would be asking for an error.
       * The Text/Email controls are disabled in the panel and say why.
       */
      const payload = { proposalId, channels: [] };
      if (acknowledgedMissing) payload.acknowledgedMissingCategories = acknowledgedMissing;
      // ⛔ Explicit. The server refuses a second send unless this is set, so
      // revoking a live patient link is never a side effect of a stray click.
      if (resend) payload.resend = true;
      // ONE reason, whatever it is for — an override, a resend, or both at
      // once. The server requires it only when replacing a link the patient
      // has already opened, and caps it at 200 characters.
      if (reasonText.trim()) payload.reason = reasonText.trim();

      const r = await window.zignagenixAPI.invoke(
        "treatment-proposals:send-to-patient", payload);

      // ⚠ NOT an exception. A refused send is an expected outcome with a
      // list attached; throwing would lose the list and render it as a red
      // toast the operator can do nothing about.
      /**
       * ⛔⛔ ALREADY_SENT IS AN OUTCOME, NOT AN EXCEPTION — review item 1.
       *
       * A live link already exists for this proposal. Throwing would render
       * it as a red toast and lose who sent it and when, leaving the operator
       * with no way forward except clicking Send again — which is the loop
       * this refusal exists to break.
       */
      if (r && r.error && r.code === "REASON_REQUIRED") {
        // Not an exception: the operator simply has to say why. Keep the
        // Resend panel on screen with the box highlighted.
        setReasonRequired(true);
        return;
      }
      if (r && r.error && r.code === "ALREADY_SENT") {
        /**
         * ⚠ ORDER MATTERS: refresh FIRST, then apply the server's answer.
         *
         * loadSendState() re-derives `alreadySent` from the token list, so
         * setting it before awaiting that would have the reload overwrite it
         * — and in the race this branch exists for (another operator sent it
         * between page load and this click) the list can disagree with the
         * server's fresher refusal. The refusal is authoritative, so it is
         * applied last.
         */
        await loadSendState(proposalId);
        setAlreadySent({
          sentAt: r.sentAt, sentByName: r.sentByName,
          expiresAt: r.expiresAt, activeTokenCount: r.activeTokenCount,
          message: r.message || "",
        });
        return;
      }
      if (r && r.error && r.code === "DOCUMENTS_MISSING") {
        /**
         * ⛔⛔ REMEMBER WHETHER THIS WAS A RESEND — review item 3.
         *
         * The override used to call back WITHOUT `resend`, so the server
         * answered ALREADY_SENT, whose panel offers Resend, whose click hits
         * the document gate again — a loop with no way out. The flag is
         * carried so one action means "resend, acknowledging these missing
         * documents, reason X".
         */
        setSendBlocked({
          missingCategories: r.missingCategories || [],
          message: r.message || "",
          resend: resend || r.resendRequired === true,
        });
        await loadSendState(proposalId);
        return;
      }
      if (r && r.error) throw new Error(r.message || r.code);

      setSendBlocked(null);
      setAlreadySent(null);
      setReasonRequired(false);
      setReasonText("");
      setSendResult(r);
      setCopied(false);
      /**
       * ⚠ THE MESSAGE SAYS WHAT ACTUALLY HAPPENED. An earlier cut promised
       * "the dispatcher sends within 15 minutes" — a promise this screen
       * could not keep even when messaging existed. Nothing is sent at all
       * now, so the toast names the one thing the operator must do.
       */
      const without = (r.missingCategories || []).length
        ? ` Sent WITHOUT: ${r.missingCategories.join(", ")}.`
        : "";
      flashOk(`Link created. Copy it and send it to the patient yourself.${without}`);
      // Refreshes the proposal body AND the token list in one pass.
      await expandProposal(proposalId);
    } catch (e) {
      flashErr(`Send failed: ${e.message}`);
    } finally { setSendBusy(false); }
  }

  async function revokeProposalToken(tokenId, proposalId) {
    if (!canWriteProposal) return;
    const reason = window.prompt(
      "Why is this link being revoked? (suspected leak, patient request, sent to the wrong person…)");
    if (!reason || !reason.trim()) return;
    setSendBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke(
        "proposal-tokens:revoke", { tokenId, reason: reason.trim() });
      if (r && r.error) throw new Error(r.message || r.code);
      // The revoked link must stop being offered for copy in this session too
      // — otherwise the panel keeps a dead link on screen next to a Copy button.
      if (sendResult && sendResult.tokenId === tokenId) setSendResult(null);
      flashOk("Link revoked. It no longer opens the proposal.");
      await loadSendState(proposalId);
    } catch (e) {
      flashErr(`Revoke failed: ${e.message}`);
    } finally { setSendBusy(false); }
  }

  /**
   * The reason input, rendered in both the override panel and the resend
   * panel. One function so the placeholder, the cap and the "no health
   * details" line cannot say different things in the two places.
   */
  function reasonBox({ testId, required, tone, border }) {
    return (
      <label style={{ display: "block", marginTop: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: tone }}>
          Reason {required ? "(required)" : "(optional)"} &mdash; recorded in the audit trail
        </span>
        <input
          type="text"
          data-testid={testId}
          value={reasonText}
          onChange={e => setReasonText(e.target.value)}
          placeholder={REASON_PLACEHOLDER}
          maxLength={REASON_MAX_LENGTH}
          style={{
            width: "100%", marginTop: 4, padding: "6px 8px",
            fontSize: 12,
            border: `1px solid ${reasonRequired && !reasonText.trim() ? "#DC3545" : border}`,
            borderRadius: 4, boxSizing: "border-box",
          }}
        />
        <span style={{ fontSize: 10, color: tone, opacity: 0.85 }}>
          {REASON_HELPER}
          {reasonRequired && !reasonText.trim() && (
            <strong data-testid="send-reason-required"> A reason is required to replace a link the patient has opened.</strong>
          )}
        </span>
      </label>
    );
  }

  async function copyProposalLink(link) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      flashErr("Could not copy automatically — select the link and copy it.");
    }
  }

  async function openProposalDocument(docId) {
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposal-documents:get-url", docId);
      if (r && r.error) throw new Error(r.message || r.code);
      if (r && r.url) window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      flashErr(`Could not open document: ${e.message}`);
    }
  }

  async function createProposal() {
    if (!candidacyAllowsProposal || busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:create", { contactId });
      if (r && r.error) throw new Error(r.reason || r.message || r.code);
      flashOk("Draft proposal created. Add at least one option to continue.");
      await reload();
      // Auto-expand the new draft so the doctor can start adding options
      if (r.id) await expandProposal(r.id);
    } catch (e) { flashErr(`Create failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  async function addProposalOption(proposalId) {
    if (busy) return;
    const priceDollars = Number(optDraft.priceDollars);
    if (!optDraft.modality || !optDraft.doseDescription || !Number.isFinite(priceDollars) || priceDollars <= 0) {
      flashErr("Modality, dose description, and a positive price are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:options:add", {
        proposalId,
        modalityCategory: optDraft.modalityCategory,
        modality: optDraft.modality,
        biologicSource: optDraft.biologicSource || null,
        doseDescription: optDraft.doseDescription,
        priceCents: Math.round(priceDollars * 100),
      });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk(`Added ${r.label}.`);
      // Reset draft and reload
      setOptDraft({ modalityCategory: optDraft.modalityCategory, modality: "", biologicSource: "", doseDescription: "", priceDollars: "" });
      await expandProposal(proposalId);
    } catch (e) { flashErr(`Add option failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  async function removeProposalOption(proposalId, optionId) {
    if (busy) return;
    if (!window.confirm("Remove this option from the proposal?")) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:options:remove", { optionId });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk("Option removed.");
      await expandProposal(proposalId);
    } catch (e) { flashErr(`Remove failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  async function setRecommendedOption(proposalId, optionId) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:options:set-recommended", {
        proposalId, optionId,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      await expandProposal(proposalId);
    } catch (e) { flashErr(`Set recommended failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  async function saveProposalNotes(proposalId, notes) {
    if (busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:set-notes", { proposalId, notes });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk("Notes saved.");
      await expandProposal(proposalId);
    } catch (e) { flashErr(`Save notes failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  async function generateTreatmentPlan(proposalId) {
    // treatment_proposal:sign — the permission the handler actually checks.
    if (!canSignProposal || busy) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:mark-ready-for-patient", { proposalId });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk("Treatment plan generated. You can now send the proposal to the patient.");
      await reload();
      await expandProposal(proposalId);
    } catch (e) { flashErr(`Generate failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  // Task #100 — Phase 4f: physician counter-sign handler. Calls the IPC
  // channel landed in Task #98 (Phase 4d). Refreshes the proposal on
  // success so the UI immediately reflects the 'fully_signed' status.
  async function counterSignProposal(proposalId) {
    if (counterSignDraft.signing || busy) return;
    const typed = (counterSignDraft.typedName || "").trim();
    if (!typed) { flashErr("Type your full legal name to counter-sign."); return; }
    if (!counterSignDraft.esignConsent) {
      flashErr("Acknowledge the electronic signature consent to counter-sign.");
      return;
    }
    setCounterSignDraft(d => ({ ...d, signing: true }));
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposal-documents:counter-sign", {
        proposalId,
        typed_name: typed,
        consent_acknowledged: true,
      });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk(`Counter-signed ${r.document_count} document(s). Proposal status: fully_signed.`);
      // Reset the form + refresh.
      setCounterSignDraft({ typedName: "", esignConsent: false, signing: false });
      await reload();
      await expandProposal(proposalId);
    } catch (e) {
      flashErr(`Counter-sign failed: ${e.message}`);
      setCounterSignDraft(d => ({ ...d, signing: false }));
    }
  }

  async function cancelProposal(proposalId) {
    if (busy) return;
    const reason = window.prompt("Reason for cancelling this proposal?");
    if (!reason) return;
    setBusy(true);
    try {
      const r = await window.zignagenixAPI.invoke("treatment-proposals:cancel", { proposalId, reason });
      if (r && r.error) throw new Error(r.message || r.code);
      flashOk("Proposal cancelled.");
      setExpandedProposalId(null);
      setProposalDetail(null);
      await reload();
    } catch (e) { flashErr(`Cancel failed: ${e.message}`); }
    finally { setBusy(false); }
  }

  // ─── Render ────────────────────────────────────────────────────────
  /*
    ⛔⛔ `loading && !hasLoaded`, NOT `loading` — Card CRM-SCROLL-JUMP review
    round 1 (P64a), 9 Oct 2026. Same defect, same shape, same page as the one
    this card was opened for.

    Every action on this tab calls the loader when it succeeds, and the loader
    sets loading=true. A bare `if (loading)` therefore replaced the ENTIRE tab
    with a centred spinner on every save — the tree unmounted, the scroll
    container's content vanished and its scrollTop was clamped to 0, and the
    staff member was returned to the top of the form mid-task.

    ⚠ A FIRST load still gets the spinner, because there is genuinely nothing to
    show. A REFRESH leaves the existing content on screen and updates underneath
    it, which is what an autosave should look like.

    ⚠ An explicit flag rather than "is some payload non-null": on this tab a null
    payload is a REAL state (no journey row yet, no candidacy recorded), so
    keying the gate on one would blank the tab for exactly the contacts that have
    not been set up — precisely the ones staff are working on.
  */
  if (loading && !hasLoaded) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#6C757D" }}>
        <Loader2 size={20} className="spin" style={{ verticalAlign: "middle" }} />
        <span style={{ marginLeft: 8 }}>Loading physician workflow…</span>
      </div>
    );
  }

  if (!reference) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#6C757D" }}>
        Physician workflow unavailable. (You may not have <code>physician:read</code> permission.)
      </div>
    );
  }

  const readOnly = !canWrite && !canSign;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/*
        ⛔⛔ CARD P100b ITEM 3 — THE LINK, AT THE TOP OF THE WORKFLOW.
        Everything below this line — candidacy, the safety screen, proposals —
        is written against the CONTACT. If there is no patient record behind
        it the work has nowhere to land and lead-intake:convert-to-emr refuses
        outright, so the doctor should be told here rather than discovering it
        at the end.
        ⚠ The SAME control and the SAME state as the header: a second copy of
        "is this linked" is how two surfaces start disagreeing.
      */}
      <div style={{
        display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
        padding: "8px 12px", background: "#F8F9FA", borderRadius: 6,
      }}>
        <span style={{ fontSize: 13, color: "#495057" }}>Patient record:</span>
        <PatientLinkControl
          link={patientLink}
          contact={contactData}
          canCreatePatient={canCreatePatient}
          canLink={canLinkPatient}
          onOpenPatient={onOpenPatient}
          onChanged={onPatientLinkChanged}
        />
      </div>

      {/* ─── Toast strip ─── */}
      {(errorMsg || okMsg) && (
        <div style={{
          padding: "10px 14px", borderRadius: 8,
          background: errorMsg ? "#FEE2E2" : "#DCFCE7",
          color: errorMsg ? "#991B1B" : "#166534",
          border: `1px solid ${errorMsg ? "#FCA5A5" : "#86EFAC"}`,
          fontSize: 13,
        }}>
          {errorMsg ? <><AlertTriangle size={14} style={{ verticalAlign: "middle" }} /> {errorMsg}</>
                    : <><CheckCircle2 size={14} style={{ verticalAlign: "middle" }} /> {okMsg}</>}
        </div>
      )}

      {/* ─── Section 1: Candidacy ─── */}
      <Card title={<><Stethoscope size={16} /> Candidacy Assessment</>}>

        {!candidacy && (
          <div style={{ padding: "16px 0" }}>
            <div style={{ color: "#6C757D", fontSize: 13, marginBottom: 12 }}>
              No candidacy assessment exists for this patient yet. Start an assessment
              to screen safety exclusions and record the verdict.
            </div>
            {canWrite && (
              <button
                onClick={startCandidacy}
                disabled={busy}
                style={btnPrimary()}
                title="Create an unsigned assessment and seed the 11 safety-screen rows"
              >
                <Sparkles size={14} /> Start candidacy assessment
              </button>
            )}
            {!canWrite && (
              <div style={{ color: "#ADB5BD", fontSize: 12 }}>
                Requires <code>physician:write</code> permission.
              </div>
            )}
          </div>
        )}

        {candidacy && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Status header */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{
                background: VERDICT_COLOR[candidacy.verdict] || "#6C757D",
                color: "white", padding: "3px 10px", borderRadius: 999,
                fontSize: 11, fontWeight: 700,
              }}>
                {VERDICT_LABEL[candidacy.verdict] || candidacy.verdict}
              </span>
              {candidacy.signed_at
                ? <span style={{ color: "#62BB46", fontSize: 12 }}>
                    <CheckCircle2 size={13} style={{ verticalAlign: "middle" }} /> Signed{" "}
                    {new Date(candidacy.signed_at).toLocaleDateString()}
                  </span>
                : <span style={{ color: "#F26522", fontSize: 12 }}>
                    <Clock size={13} style={{ verticalAlign: "middle" }} /> Draft (unsigned)
                  </span>}
            </div>

            {/* Absolute exclusions */}
            <div>
              <div style={sectionHeader()}>
                <Shield size={13} /> Absolute exclusions (mandatory screening)
              </div>
              {(reference.absolute || []).map(e => (
                <ExclusionRow
                  key={e.code}
                  label={e.label}
                  code={e.code}
                  status={screenByCode.get(e.code)?.status || "unscreened"}
                  onChange={s => setExclusionStatus(e.code, s)}
                  disabled={!canWrite || !!candidacy.signed_at}
                  isAbsolute
                />
              ))}
            </div>

            {/* Relative exclusions */}
            <div>
              <div style={sectionHeader()}>
                <Shield size={13} /> Relative exclusions (recommended)
              </div>
              {(reference.relative || []).map(e => (
                <ExclusionRow
                  key={e.code}
                  label={e.label}
                  code={e.code}
                  status={screenByCode.get(e.code)?.status || "unscreened"}
                  onChange={s => setExclusionStatus(e.code, s)}
                  disabled={!canWrite || !!candidacy.signed_at}
                />
              ))}
            </div>

            {/* Verdict picker + sign */}
            {!candidacy.signed_at && (
              <div style={{ borderTop: "1px solid #DEE2E6", paddingTop: 12 }}>
                <label style={fieldLabel()}>Verdict</label>
                <select
                  value={verdict}
                  onChange={e => setVerdict(e.target.value)}
                  disabled={!canSign}
                  style={fieldSelect()}
                >
                  <option value="pending">Pending (cannot sign)</option>
                  <option value="yes">Candidate</option>
                  <option value="no">Not a candidate</option>
                  <option value="conditional">Conditional</option>
                </select>

                {verdict === "conditional" && (
                  <>
                    <label style={fieldLabel()}>Conditional requirements</label>
                    <textarea
                      value={conditional}
                      onChange={e => setConditional(e.target.value)}
                      placeholder="e.g. Candidate once A1C below 8 and steroid taper complete."
                      disabled={!canSign}
                      style={fieldTextarea()}
                      rows={2}
                    />
                  </>
                )}

                <label style={fieldLabel()}>Clinical rationale</label>
                <textarea
                  value={rationale}
                  onChange={e => setRationale(e.target.value)}
                  placeholder="Why this verdict — clinical reasoning the PEM and any reviewing physician can read."
                  disabled={!canSign}
                  style={fieldTextarea()}
                  rows={3}
                />

                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
                  <button
                    onClick={signCandidacy}
                    disabled={!canSign || !isSignable || busy || verdict === "pending"}
                    style={isSignable && verdict !== "pending" ? btnPrimary() : btnDisabled()}
                    title={!isSignable
                      ? "Screen all 6 absolute exclusions before signing"
                      : verdict === "pending" ? "Pick a verdict to sign" : "Sign this candidacy"}
                  >
                    <FileSignature size={14} /> Sign candidacy
                  </button>
                  {!isSignable && (
                    <span style={{ fontSize: 11, color: "#F26522" }}>
                      <AlertTriangle size={11} style={{ verticalAlign: "middle" }} />{" "}
                      {(reference.absolute || []).length - (screen || []).filter(r => absoluteCodes.includes(r.exclusion_code) && r.status !== "unscreened").length} absolute exclusion(s) still unscreened
                    </span>
                  )}
                </div>
                {!canSign && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "#6C757D" }}>
                    Signing requires <code>physician:sign</code> permission.
                  </div>
                )}
              </div>
            )}

            {/* Signed assessment — read-only summary */}
            {candidacy.signed_at && (
              <div style={{ background: "#F8F9FA", padding: 12, borderRadius: 8, fontSize: 12 }}>
                <div><strong>Verdict:</strong> {VERDICT_LABEL[candidacy.verdict]}</div>
                {candidacy.conditional_requirements && (
                  <div><strong>Conditional requirements:</strong> {candidacy.conditional_requirements}</div>
                )}
                {candidacy.rationale && (
                  <div style={{ marginTop: 4 }}><strong>Rationale:</strong> {candidacy.rationale}</div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ─── Section 2: Treatment plan ─── */}
      <Card title={<><Sparkles size={16} /> Treatment Plan</>}>
        {!candidacy?.signed_at && (
          <div style={{ background: "#FEF3C7", padding: 10, borderRadius: 6, fontSize: 12, color: "#92400E", marginBottom: 12 }}>
            <AlertTriangle size={13} style={{ verticalAlign: "middle" }} />{" "}
            Sign candidacy first. Treatment plans should be authored after the verdict is on record.
          </div>
        )}

        {canWrite && candidacy?.verdict === "yes" && (
          <div style={{ borderTop: "1px dashed #DEE2E6", paddingTop: 12, marginBottom: 16 }}>
            <div style={sectionHeader()}>New treatment recommendation</div>
            <label style={fieldLabel()}>Category</label>
            <select
              value={planCategory}
              onChange={e => { setPlanCategory(e.target.value); setPlanModality(""); }}
              style={fieldSelect()}
            >
              {(reference.treatment_catalog ? Object.keys(reference.treatment_catalog) : []).map(c => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>

            <label style={fieldLabel()}>Modality</label>
            <select
              value={planModality}
              onChange={e => setPlanModality(e.target.value)}
              style={fieldSelect()}
            >
              <option value="">— select —</option>
              {availableModalities.map(m => (
                <option key={m} value={m}>{m.replace(/_/g, " ")}</option>
              ))}
            </select>

            <label style={fieldLabel()}>Biologic source</label>
            <input
              type="text"
              value={planBiologic}
              onChange={e => setPlanBiologic(e.target.value)}
              placeholder="e.g. Wharton's jelly MSCs, autologous PRP"
              style={fieldInput()}
            />

            <label style={fieldLabel()}>Dose description</label>
            <input
              type="text"
              value={planDose}
              onChange={e => setPlanDose(e.target.value)}
              placeholder="e.g. 100M IV + 50M intra-articular knee"
              style={fieldInput()}
            />

            <label style={fieldLabel()}>Clinical rationale</label>
            <textarea
              value={planRationale}
              onChange={e => setPlanRationale(e.target.value)}
              placeholder="Why this modality and dose — the clinical reasoning the PEM will reference during the financial conversation."
              style={fieldTextarea()}
              rows={3}
            />

            <button onClick={savePlanDraft} disabled={busy || !planModality} style={btnPrimary()}>
              <Save size={14} /> Save draft
            </button>
          </div>
        )}

        {/* Plan history */}
        <div style={sectionHeader()}>Plan history</div>
        {plans.length === 0 && (
          <div style={{ color: "#ADB5BD", fontSize: 12, padding: "8px 0" }}>
            No treatment recommendations yet.
          </div>
        )}
        {plans.map(p => (
          <div key={p.id} style={{
            padding: 12, borderRadius: 8, marginBottom: 6,
            background: p.is_active ? "#E6F7F9" : "#F8F9FA",
            border: p.is_active ? "1px solid #2E8B98" : "1px solid #E9ECEF",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <strong style={{ fontSize: 13 }}>
                {p.modality_category} &middot; {p.modality.replace(/_/g, " ")}
              </strong>
              {p.is_active
                ? <span style={{ background: "#2E8B98", color: "white", fontSize: 10, padding: "2px 8px", borderRadius: 999, fontWeight: 700 }}>ACTIVE</span>
                : p.signed_at
                  ? <span style={{ background: "#6C757D", color: "white", fontSize: 10, padding: "2px 8px", borderRadius: 999 }}>Superseded</span>
                  : <span style={{ background: "#F26522", color: "white", fontSize: 10, padding: "2px 8px", borderRadius: 999 }}>Draft</span>}
            </div>
            {p.biologic_source && <div style={{ fontSize: 12 }}><strong>Biologic:</strong> {p.biologic_source}</div>}
            {p.dose_description && <div style={{ fontSize: 12 }}><strong>Dose:</strong> {p.dose_description}</div>}
            {p.rationale && <div style={{ fontSize: 11, color: "#6C757D", marginTop: 4 }}>{p.rationale}</div>}
            {!p.signed_at && canSign && (
              <button
                onClick={() => signPlan(p.id)}
                disabled={busy}
                style={{ ...btnPrimary(), marginTop: 8, fontSize: 11 }}
              >
                <FileSignature size={12} /> Sign this plan
              </button>
            )}
          </div>
        ))}
      </Card>

      {/* ─── Section 3: Treatment Proposal (Task #77, Phase 1) ─── */}
      <Card title={<><FileText size={16} /> Treatment Proposal</>}>
        {!candidacyAllowsProposal ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 12, background: "#F8F9FA", borderRadius: 8, color: "#6C757D", fontSize: 12 }}>
            <Lock size={16} />
            <div>
              Treatment proposals unlock after candidacy is signed as <b>Yes</b>.
              {candidacy && candidacy.verdict === "conditional" && (
                <div style={{ marginTop: 4, color: "#F26522" }}>
                  Conditional candidacy doesn't pass the gate &mdash; PEM must coordinate
                  remediation, then physician re-signs verdict=yes.
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* List of existing proposals */}
            <div style={sectionHeader()}>Proposals for this contact</div>
            {proposals.length === 0 && (
              <div style={{ color: "#ADB5BD", fontSize: 12, padding: "8px 0" }}>
                No proposals yet. Click <b>Build new proposal</b> to start.
              </div>
            )}
            {proposals.map(pr => {
              const isExpanded = expandedProposalId === pr.id;
              const isDraft = pr.status === "draft";
              /**
               * ⛔ EXPIRY IS NOT A STATUS — Card P77. Nothing in the codebase
               * ever writes status='expired' (measured: no handler, no script,
               * no cron). A proposal 30 days past expires_at still reads
               * 'ready_for_patient'. So "ready" for UI purposes means ready
               * AND not past its expiry — otherwise the Send panel offers to
               * mint a token whose TTL is copied from an expiry already in the
               * past, and the patient gets REJECT_EXPIRED from a link the
               * operator was just told was sent.
               */
              const isPastExpiry = !!pr.expires_at && new Date(pr.expires_at).getTime() < Date.now();
              const isReady = pr.status === "ready_for_patient";
              // Daniel's ruling, 11 Oct 2026: once the patient has opened the
              // proposal, staff may RESEND (revoking the link the patient
              // holds) but may not make a first Send. The panel therefore
              // lives in both states; the server decides which action is
              // legal in each.
              const isReviewing = pr.status === "patient_reviewing";
              const isTerminal = ["expired", "cancelled", "paid_in_full"].includes(pr.status);
              const expiresIn = pr.expires_at
                ? Math.max(0, Math.ceil((new Date(pr.expires_at).getTime() - Date.now()) / (24*60*60*1000)))
                : null;
              return (
                <div key={pr.id} style={{
                  background: isExpanded ? "#E6F7F9" : "#F8F9FA",
                  border: isExpanded ? "1px solid #2E8B98" : "1px solid #E9ECEF",
                  borderRadius: 8, padding: 12, marginBottom: 8,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                       onClick={() => isExpanded ? setExpandedProposalId(null) : expandProposal(pr.id)}>
                    <div>
                      <strong style={{ fontSize: 13 }}>Proposal #{pr.id}</strong>
                      <span style={{
                        marginLeft: 8, background: proposalStatusColor(pr.status),
                        color: "white", fontSize: 10, padding: "2px 8px", borderRadius: 999, fontWeight: 700,
                      }}>{pr.status}</span>
                      {pr.total_amount_cents > 0 && (
                        <span style={{ marginLeft: 8, fontSize: 12, color: "#6C757D" }}>
                          ${(pr.total_amount_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: expiresIn != null && expiresIn < 3 ? "#DC3545" : "#6C757D" }}>
                      {isTerminal
                        ? new Date(pr.updated_at).toLocaleDateString()
                        : expiresIn != null && (expiresIn === 0
                            ? "expires today"
                            : `expires in ${expiresIn} day${expiresIn !== 1 ? "s" : ""}`)}
                    </div>
                  </div>

                  {/* Expanded detail panel */}
                  {isExpanded && proposalDetail && proposalDetail.proposal && proposalDetail.proposal.id === pr.id && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #DEE2E6" }}>

                      {/* Task #100 — Phase 4f: counter-sign card (only for patient_signed status). */}
                      {pr.status === "patient_signed" && (
                        <div style={{
                          background: "#FEF3C7", border: "2px solid #F59E0B",
                          borderRadius: 10, padding: 14, marginBottom: 14,
                        }}>
                          <div style={{
                            fontSize: 13, fontWeight: 700, color: "#92400E",
                            display: "flex", alignItems: "center", gap: 6, marginBottom: 8,
                          }}>
                            <Star size={14} /> Patient signed — counter-sign required
                          </div>
                          <div style={{ fontSize: 12, color: "#78350F", lineHeight: 1.6, marginBottom: 12 }}>
                            The patient has e-signed every required document. As the
                            prescribing physician, you must counter-sign before the
                            proposal advances to payment.
                            {pr.patient_signed_at && (
                              <> Patient signed on <strong>{new Date(pr.patient_signed_at).toLocaleString()}</strong>.</>
                            )}
                          </div>
                          <label style={{ display: "block", marginBottom: 8 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase" }}>
                              Type your full legal name
                            </span>
                            <input
                              type="text"
                              value={counterSignDraft.typedName}
                              onChange={(e) => setCounterSignDraft({ ...counterSignDraft, typedName: e.target.value })}
                              placeholder="e.g. Dr. Karely Vega"
                              autoComplete="name"
                              style={{
                                width: "100%", marginTop: 4,
                                padding: "8px 10px", fontSize: 14,
                                fontFamily: "'Brush Script MT', cursive, 'Apple Chancery'",
                                border: `2px solid ${counterSignDraft.typedName.trim() ? "#F59E0B" : "#E9ECEF"}`,
                                borderRadius: 6, outline: "none", boxSizing: "border-box",
                                minHeight: 36,
                              }}
                            />
                          </label>
                          <label style={{
                            display: "flex", alignItems: "flex-start", gap: 8,
                            cursor: "pointer", marginBottom: 12, fontSize: 12, color: "#78350F",
                            lineHeight: 1.5,
                          }}>
                            <input
                              type="checkbox"
                              checked={counterSignDraft.esignConsent}
                              onChange={(e) => setCounterSignDraft({ ...counterSignDraft, esignConsent: e.target.checked })}
                              style={{ marginTop: 3, width: 18, height: 18, accentColor: "#F59E0B", cursor: "pointer", flexShrink: 0 }}
                            />
                            <span>
                              I consent to use electronic signatures and electronic records
                              for this proposal, in accordance with NOM-151-SCFI-2016 (Mexico)
                              and 15 U.S.C. § 7001 (United States ESIGN Act).
                            </span>
                          </label>
                          <button
                            type="button"
                            onClick={() => counterSignProposal(pr.id)}
                            disabled={busy || counterSignDraft.signing || !counterSignDraft.typedName.trim() || !counterSignDraft.esignConsent}
                            style={{
                              width: "100%", padding: "10px 14px",
                              background: (counterSignDraft.signing || !counterSignDraft.typedName.trim() || !counterSignDraft.esignConsent)
                                ? "#CBD5E1" : "#F59E0B",
                              color: "white", border: "none", borderRadius: 6,
                              fontSize: 13, fontWeight: 700,
                              cursor: (counterSignDraft.signing || !counterSignDraft.typedName.trim() || !counterSignDraft.esignConsent)
                                ? "not-allowed" : "pointer",
                              minHeight: 36,
                            }}
                          >
                            {counterSignDraft.signing ? "Counter-signing…" : "Counter-sign all documents"}
                          </button>
                        </div>
                      )}

                      {/* Phase 4f: fully-signed confirmation */}
                      {pr.status === "fully_signed" && (
                        <div style={{
                          background: "#D1FAE5", border: "2px solid #62BB46",
                          borderRadius: 10, padding: 12, marginBottom: 14,
                          fontSize: 13, color: "#065F46",
                        }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>
                            ✓ Fully signed — ready for payment
                          </div>
                          {pr.physician_signed_at && (
                            <div style={{ fontSize: 11, color: "#047857" }}>
                              Counter-signed on {new Date(pr.physician_signed_at).toLocaleString()}.
                            </div>
                          )}
                        </div>
                      )}

                      {/* Options list */}
                      <div style={sectionHeader()}>Options ({proposalDetail.options.length})</div>
                      {proposalDetail.options.length === 0 && (
                        <div style={{ color: "#ADB5BD", fontSize: 12, padding: "6px 0" }}>
                          No options yet. Add at least one below.
                        </div>
                      )}
                      {proposalDetail.options.map(o => (
                        <div key={o.id} style={{
                          padding: 10, marginBottom: 6, borderRadius: 6,
                          background: o.is_recommended ? "#FEF3C7" : "white",
                          border: o.is_recommended ? "1px solid #F59E0B" : "1px solid #E9ECEF",
                          display: "flex", alignItems: "center", gap: 10,
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>
                              {o.label}: {o.modality.replace(/_/g, " ")} {o.dose_description}
                              {o.is_recommended && (
                                <span style={{
                                  marginLeft: 6, background: "#F59E0B", color: "white", fontSize: 9,
                                  padding: "1px 6px", borderRadius: 999, fontWeight: 700,
                                }}>RECOMMENDED</span>
                              )}
                            </div>
                            {o.biologic_source && <div style={{ fontSize: 11, color: "#6C757D" }}>{o.biologic_source}</div>}
                            <div style={{ fontSize: 12, marginTop: 2 }}>
                              <strong>${(o.price_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                            </div>
                          </div>
                          {isDraft && (
                            <div style={{ display: "flex", gap: 4 }}>
                              {!o.is_recommended && (
                                <button onClick={() => setRecommendedOption(pr.id, o.id)} disabled={busy} style={btnGhost()} title="Mark as recommended option">
                                  <Star size={12} />
                                </button>
                              )}
                              <button onClick={() => removeProposalOption(pr.id, o.id)} disabled={busy} style={{ ...btnGhost(), color: "#DC3545" }}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Add option form (only when draft) */}
                      {isDraft && canWrite && (
                        <div style={{
                          marginTop: 8, padding: 12, background: "#FAFBFC",
                          borderRadius: 8, border: "1px dashed #CED4DA",
                        }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", marginBottom: 6 }}>ADD OPTION</div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                            <div>
                              <label style={fieldLabel()}>Category</label>
                              <select value={optDraft.modalityCategory}
                                onChange={e => setOptDraft({ ...optDraft, modalityCategory: e.target.value })}
                                style={fieldSelect()}>
                                <option value="orthopedic">Orthopedic</option>
                                <option value="neurologic">Neurologic</option>
                                <option value="autoimmune">Autoimmune</option>
                                <option value="longevity">Longevity</option>
                              </select>
                            </div>
                            <div>
                              <label style={fieldLabel()}>Modality</label>
                              <input type="text" value={optDraft.modality}
                                onChange={e => setOptDraft({ ...optDraft, modality: e.target.value })}
                                placeholder="e.g. MSC_stem_cells"
                                style={fieldInput()} />
                            </div>
                            <div>
                              <label style={fieldLabel()}>Biologic source</label>
                              <input type="text" value={optDraft.biologicSource}
                                onChange={e => setOptDraft({ ...optDraft, biologicSource: e.target.value })}
                                placeholder="e.g. Wharton's jelly MSC"
                                style={fieldInput()} />
                            </div>
                            <div>
                              <label style={fieldLabel()}>Dose</label>
                              <input type="text" value={optDraft.doseDescription}
                                onChange={e => setOptDraft({ ...optDraft, doseDescription: e.target.value })}
                                placeholder="e.g. 150 million cells IV"
                                style={fieldInput()} />
                            </div>
                            <div>
                              <label style={fieldLabel()}>Price ($)</label>
                              <input type="number" value={optDraft.priceDollars}
                                onChange={e => setOptDraft({ ...optDraft, priceDollars: e.target.value })}
                                placeholder="12350"
                                style={fieldInput()} />
                            </div>
                          </div>
                          <button onClick={() => addProposalOption(pr.id)} disabled={busy} style={{ ...btnPrimary(), marginTop: 8 }}>
                            <Plus size={12} /> Add option
                          </button>
                        </div>
                      )}

                      {/* Physician notes */}
                      <div style={{ marginTop: 14 }}>
                        <label style={fieldLabel()}>Physician notes (shown to patient)</label>
                        <textarea
                          defaultValue={proposalDetail.proposal.physician_notes || ""}
                          disabled={!isDraft || !canWrite}
                          rows={3}
                          style={fieldTextarea()}
                          placeholder="e.g. I recommend 150 million MSCs for optimal outcomes. 100 million is acceptable if budget is a concern."
                          onBlur={e => {
                            if (isDraft && canWrite && e.target.value !== (proposalDetail.proposal.physician_notes || "")) {
                              saveProposalNotes(pr.id, e.target.value);
                            }
                          }}
                        />
                      </div>

                      {/* Totals (computed by save-draft) */}
                      {proposalDetail.proposal.total_amount_cents > 0 && (
                        <div style={{
                          marginTop: 12, padding: 10, background: "white", borderRadius: 6,
                          border: "1px solid #E9ECEF", fontSize: 12,
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Total (recommended option):</span>
                            <strong>${(proposalDetail.proposal.total_amount_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", color: "#6C757D" }}>
                            <span>Deposit (50%):</span>
                            <span>${(proposalDetail.proposal.deposit_amount_cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      {isDraft && (
                        <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                          <button
                            onClick={async () => {
                              setBusy(true);
                              try {
                                const r = await window.zignagenixAPI.invoke("treatment-proposals:save-draft", { proposalId: pr.id });
                                if (r && r.error) throw new Error(r.message || r.code);
                                flashOk(`Draft saved. Total $${(r.subtotal_cents/100).toFixed(2)}, deposit $${(r.deposit_cents/100).toFixed(2)}.`);
                                await expandProposal(pr.id);
                              } catch (e) { flashErr(`Save failed: ${e.message}`); }
                              finally { setBusy(false); }
                            }}
                            disabled={busy || !canWrite}
                            style={btnGhost()}
                          >
                            <Save size={12} /> Save draft
                          </button>

                          {/* ⛔ GATED ON treatment_proposal:sign, NOT physician:sign.
                              The handler behind this button calls
                              rbac.secureHandler("treatment_proposal:sign"); gating
                              on canSign enabled it for treating_physician and
                              chief_medical_director, who hold physician:sign and
                              are then refused by the server. See ContactDetailView. */}
                          <button
                            onClick={() => generateTreatmentPlan(pr.id)}
                            disabled={busy || !canSignProposal
                              || proposalDetail.options.length === 0
                              || !proposalDetail.options.some(o => o.is_recommended)}
                            style={!canSignProposal || proposalDetail.options.length === 0 || !proposalDetail.options.some(o => o.is_recommended)
                              ? btnDisabled()
                              : btnPrimary()}
                            title={
                              !canSignProposal
                                ? "Requires the treatment_proposal:sign permission"
                                : proposalDetail.options.length === 0
                                  ? "Add at least one option first"
                                  : !proposalDetail.options.some(o => o.is_recommended)
                                    ? "Mark one option as recommended first"
                                    : "Transition to ready_for_patient, then send the proposal to the patient"
                            }
                          >
                            <Send size={12} /> Generate Treatment Plan
                          </button>

                          <button onClick={() => cancelProposal(pr.id)} disabled={busy || !canWrite}
                            style={{ ...btnGhost(), color: "#DC3545", marginLeft: "auto" }}>
                            <Trash2 size={12} /> Cancel proposal
                          </button>
                        </div>
                      )}

                      {isReady && (
                        <div style={{ marginTop: 12, padding: 10, background: "#DCFCE7", color: "#166534", borderRadius: 6, fontSize: 12 }}>
                          <CheckCircle2 size={14} style={{ verticalAlign: "middle" }} />{" "}
                          Marked ready for patient on{" "}
                          {pr.ready_for_patient_at ? new Date(pr.ready_for_patient_at).toLocaleString() : "—"}.
                        </div>
                      )}

                      {/* ─── Card P77 (item 13): Send proposal to patient ───
                          ready_for_patient — Send or Resend.
                          patient_reviewing — RESEND ONLY, with a required
                          reason (Daniel, 11 Oct 2026): the patient is reading
                          the proposal, so replacing their link is an act
                          someone will have to explain later.
                          Candidacy=yes signed is already an ancestor condition
                          (the whole card sits behind candidacyAllowsProposal). */}
                      {(isReady || isReviewing) && (
                        <div
                          data-testid="send-proposal-panel"
                          style={{
                            marginTop: 12, padding: 14,
                            background: "#F8FAFC", border: "1px solid #CBD5E1",
                            borderRadius: 10,
                          }}
                        >
                          <div style={{
                            fontSize: 13, fontWeight: 700, color: "#0F172A",
                            display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
                          }}>
                            <Link2 size={14} /> Send proposal to patient
                          </div>

                          {!canSignProposal ? (
                            <div
                              data-testid="send-proposal-no-permission"
                              style={{ fontSize: 12, color: "#6C757D", display: "flex", gap: 8, alignItems: "flex-start" }}
                            >
                              <Lock size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                              <span>
                                Sending a proposal to a patient requires the{" "}
                                <code>treatment_proposal:sign</code> permission.
                              </span>
                            </div>
                          ) : isPastExpiry ? (
                            /* ⛔ The expiry trap, surfaced rather than hit. A token
                               minted now would inherit this past expires_at and be
                               dead on arrival. */
                            <div
                              data-testid="send-proposal-expired"
                              style={{ fontSize: 12, color: "#991B1B", background: "#FEE2E2", padding: 10, borderRadius: 6, display: "flex", gap: 8, alignItems: "flex-start" }}
                            >
                              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                              <span>
                                This proposal expired on{" "}
                                <strong>{new Date(pr.expires_at).toLocaleDateString()}</strong>, so a
                                patient link created now would already be dead. Build a new
                                proposal &mdash; pricing and candidacy both have to be re-verified.
                              </span>
                            </div>
                          ) : (
                            <>
                              {/* ⛔⛔ TEXT AND EMAIL ARE OFF — Daniel's ruling, 10 Oct
                                  2026. SMS, email, WhatsApp and calls are not wired;
                                  all patient messaging goes live with the Twilio
                                  card. They are shown DISABLED with the reason
                                  rather than hidden, so the operator knows the
                                  capability is coming and does not go hunting for
                                  it elsewhere. The server refuses a requested
                                  channel outright — this is presentation, not the
                                  control. */}
                              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 6 }}>
                                <label style={{
                                  display: "flex", alignItems: "center", gap: 6, fontSize: 12,
                                  color: "#94A3B8", cursor: "not-allowed",
                                }}>
                                  <input
                                    type="checkbox"
                                    data-testid="send-channel-sms"
                                    checked={false}
                                    disabled
                                    readOnly
                                    title={CHANNEL_UNAVAILABLE_REASON}
                                    style={{ width: 15, height: 15 }}
                                  />
                                  <MessageSquare size={13} /> Text
                                </label>
                                <label style={{
                                  display: "flex", alignItems: "center", gap: 6, fontSize: 12,
                                  color: "#94A3B8", cursor: "not-allowed",
                                }}>
                                  <input
                                    type="checkbox"
                                    data-testid="send-channel-email"
                                    checked={false}
                                    disabled
                                    readOnly
                                    title={CHANNEL_UNAVAILABLE_REASON}
                                    style={{ width: 15, height: 15 }}
                                  />
                                  <Mail size={13} /> Email
                                </label>
                              </div>
                              <div
                                data-testid="send-channels-unavailable"
                                style={{
                                  fontSize: 11, color: "#92400E", background: "#FEF3C7",
                                  padding: "6px 8px", borderRadius: 4, marginBottom: 10,
                                }}
                              >
                                {CHANNEL_UNAVAILABLE_REASON}
                              </div>

                              <div style={{ fontSize: 11, color: "#64748B", lineHeight: 1.5, marginBottom: 10 }}>
                                Create the link, then copy it and send it to the patient yourself.
                                The link opens the proposal without a login, so treat it like a
                                password.
                              </div>

                              {/* ⛔⛔ ONE LIVE LINK PER PROPOSAL — review item 1.
                                  Once a link is live the bare Send button is
                                  GONE, replaced by when it was sent and an
                                  explicit Resend that names its consequence.
                                  A second live bearer token to a PHI page is
                                  strictly more exposure, and the old button
                                  invited exactly that click. */}
                              {(alreadySent || isReviewing) ? (
                                <div
                                  data-testid="send-proposal-already-sent"
                                  style={{
                                    padding: 10, background: "#ECFDF5",
                                    border: "1px solid #6EE7B7", borderRadius: 8,
                                  }}
                                >
                                  {/* ⚠ alreadySent can be NULL here: at
                                      patient_reviewing the panel is Resend-only
                                      even when every token has been revoked or
                                      has expired, so there may be no live link
                                      to describe. Reading .sentAt unguarded
                                      would crash the whole contact record. */}
                                  <div style={{ fontSize: 12, color: "#065F46", fontWeight: 700 }}>
                                    <CheckCircle2 size={13} style={{ verticalAlign: "middle" }} />{" "}
                                    {alreadySent
                                      ? <>Sent {new Date(alreadySent.sentAt).toLocaleString()}
                                          {alreadySent.sentByName ? ` by ${alreadySent.sentByName}` : ""}</>
                                      : <>The patient has opened this proposal</>}
                                  </div>
                                  <div style={{ fontSize: 11, color: "#047857", marginTop: 4, lineHeight: 1.5 }}>
                                    {alreadySent ? (
                                      <>
                                        The link is live until{" "}
                                        {new Date(alreadySent.expiresAt).toLocaleDateString()}.
                                        Resending revokes it &mdash; anyone already holding that
                                        link, including the patient, loses access.
                                      </>
                                    ) : (
                                      <>
                                        There is no live link right now. Resending issues a new
                                        one and revokes anything still outstanding.
                                      </>
                                    )}
                                  </div>
                                  {/* ⛔ Once the patient has OPENED the proposal, a
                                      reason is required to replace their link
                                      (Daniel, 11 Oct 2026). The server enforces it;
                                      this is the same predicate, not a second one.

                                      ⚠ HIDDEN WHILE THE DOCUMENT GATE IS BLOCKING.
                                      Both panels render at once in the resend +
                                      missing-documents case, and both boxes are
                                      bound to the SAME `reasonText` state — so the
                                      operator saw one sentence appear in two inputs
                                      and could not tell which one the button would
                                      read. The blocking panel owns the action, so
                                      it owns the box. */}
                                  {!sendBlocked && reasonBox({
                                    testId: "send-resend-reason",
                                    required: reasonIsRequired(pr.status, true),
                                    tone: "#78350F", border: "#FCD34D",
                                  })}
                                  <button
                                    type="button"
                                    data-testid="send-proposal-resend"
                                    onClick={() => sendProposalToPatient(pr.id, null, true)}
                                    disabled={sendBusy || busy}
                                    style={{
                                      marginTop: 8, padding: "8px 12px",
                                      background: "white", color: "#B45309",
                                      border: "1px solid #B45309", borderRadius: 6,
                                      fontSize: 12, fontWeight: 700,
                                      cursor: sendBusy || busy ? "not-allowed" : "pointer",
                                      minHeight: 34,
                                    }}
                                  >
                                    <RefreshCcw size={12} /> Resend (revokes the old link)
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  data-testid="send-proposal-button"
                                  onClick={() => sendProposalToPatient(pr.id)}
                                  disabled={sendBusy || busy}
                                  style={sendBusy || busy ? btnDisabled() : btnPrimary()}
                                >
                                  <Send size={12} /> {sendBusy ? "Sending…" : "Send proposal to patient"}
                                </button>
                              )}

                              {/* ⛔⛔ REFUSED: a required document is missing.
                                  No link was minted. The override below is the
                                  only way past, it is deliberate, and it is
                                  recorded in the audit trail by name. */}
                              {sendBlocked && (
                                <div
                                  data-testid="send-proposal-blocked"
                                  style={{
                                    marginTop: 12, padding: 12,
                                    background: "#FEF2F2", border: "2px solid #FCA5A5",
                                    borderRadius: 8,
                                  }}
                                >
                                  <div style={{
                                    fontSize: 12, fontWeight: 700, color: "#991B1B",
                                    display: "flex", alignItems: "center", gap: 6, marginBottom: 6,
                                  }}>
                                    <AlertTriangle size={14} /> Not sent &mdash; documents missing
                                  </div>
                                  <div style={{ fontSize: 12, color: "#7F1D1D", lineHeight: 1.6 }}>
                                    No patient link was created. This proposal has no{" "}
                                    <strong>{sendBlocked.missingCategories.map(c => c.replace(/_/g, " ")).join(", ")}</strong>{" "}
                                    document, so the patient would open the link and find it missing.
                                  </div>

                                  {/* The reason each category failed, where the
                                      bundle told us one. */}
                                  {docResults && (docResults.errors || []).length > 0 && (
                                    <ul
                                      data-testid="send-blocked-reasons"
                                      style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: 11, color: "#7F1D1D" }}
                                    >
                                      {docResults.errors.map(er => (
                                        <li key={er.category}>
                                          <strong>{er.category.replace(/_/g, " ")}</strong>: {er.code}
                                          {er.message ? ` — ${er.message}` : ""}
                                        </li>
                                      ))}
                                    </ul>
                                  )}

                                  {reasonBox({
                                    testId: "send-override-reason",
                                    required: reasonIsRequired(pr.status, sendBlocked.resend === true),
                                    tone: "#7F1D1D", border: "#FCA5A5",
                                  })}
                                  <button
                                    type="button"
                                    data-testid="send-proposal-override"
                                    onClick={() => sendProposalToPatient(pr.id, sendBlocked.missingCategories, sendBlocked.resend === true)}
                                    disabled={sendBusy || busy}
                                    style={{
                                      marginTop: 10, padding: "8px 12px",
                                      background: "white", color: "#B91C1C",
                                      border: "1px solid #B91C1C", borderRadius: 6,
                                      fontSize: 12, fontWeight: 700,
                                      cursor: sendBusy || busy ? "not-allowed" : "pointer",
                                      minHeight: 34,
                                    }}
                                    title="Records in the audit trail that the proposal went out without these documents"
                                  >
                                    Send without: {sendBlocked.missingCategories.map(c => c.replace(/_/g, " ")).join(", ")}
                                  </button>
                                  <div style={{ fontSize: 11, color: "#7F1D1D", marginTop: 6 }}>
                                    This is recorded against your name in the proposal's audit trail.
                                  </div>
                                </div>
                              )}
                            </>
                          )}

                          {/* ─── The one-time link ───────────────────────── */}
                          {sendResult && (
                            <div
                              data-testid="send-proposal-link"
                              style={{
                                marginTop: 12, padding: 10, background: "white",
                                border: "1px solid #2E8B98", borderRadius: 8,
                              }}
                            >
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", marginBottom: 6 }}>
                                Patient link
                              </div>
                              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                                <code style={{
                                  flex: 1, minWidth: 220, fontSize: 11, wordBreak: "break-all",
                                  background: "#F1F5F9", padding: "6px 8px", borderRadius: 4,
                                }}>{sendResult.link}</code>
                                <button
                                  type="button"
                                  data-testid="copy-proposal-link"
                                  onClick={() => copyProposalLink(sendResult.link)}
                                  style={btnGhost()}
                                >
                                  {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
                                </button>
                              </div>
                              <div style={{ fontSize: 11, color: "#92400E", background: "#FEF3C7", padding: "6px 8px", borderRadius: 4, marginTop: 8 }}>
                                Copy this now. Only a one-way hash is stored, so the link cannot be
                                shown again &mdash; reloading this page loses it and you'd have to
                                issue a new one.
                              </div>
                              {/* The queuedChannels / skippedChannels branches that
                                  lived here are GONE, not left dormant: the server
                                  no longer returns those fields at all, so they were
                                  dead markup describing a capability this build does
                                  not have. */}
                              <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
                                Expires <strong>{new Date(sendResult.expiresAt).toLocaleString()}</strong>
                              </div>
                              {sendResult.resend && (
                                <div data-testid="send-resend-note" style={{ fontSize: 11, color: "#B45309", marginTop: 4 }}>
                                  The earlier link was revoked. Anyone still holding it
                                  now gets &ldquo;link no longer valid&rdquo;.
                                </div>
                              )}
                            </div>
                          )}

                          {/* ─── Per-category document results ──────────────
                              Reported honestly: a category that failed is named
                              with its reason, never folded into a success toast. */}
                          {docResults && (
                            <div data-testid="send-doc-results" style={{ marginTop: 10, fontSize: 11, color: "#475569" }}>
                              {(docResults.generated || []).length > 0 && (
                                <div>Generated: {docResults.generated.map(g => g.category).join(", ")}</div>
                              )}
                              {(docResults.skipped || []).length > 0 && (
                                <div>Already present: {docResults.skipped.map(sk => sk.category).join(", ")}</div>
                              )}
                              {(docResults.errors || []).length > 0 && (
                                <div data-testid="send-doc-errors" style={{ color: "#B91C1C", marginTop: 4 }}>
                                  <AlertTriangle size={11} style={{ verticalAlign: "middle" }} />{" "}
                                  Could not generate:{" "}
                                  {docResults.errors.map(er => `${er.category} (${er.code})`).join(", ")}
                                  . The patient will not see these documents.
                                </div>
                              )}
                            </div>
                          )}

                          {/* ─── Documents on the proposal ─────────────────── */}
                          {proposalDocs.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", marginBottom: 6 }}>
                                Documents ({proposalDocs.length})
                              </div>
                              {proposalDocs.map(d => (
                                <div key={d.id} style={{
                                  display: "flex", alignItems: "center", gap: 8,
                                  padding: "6px 8px", background: "white",
                                  border: "1px solid #E2E8F0", borderRadius: 6, marginBottom: 4,
                                }}>
                                  <FileText size={13} color="#2E8B98" style={{ flexShrink: 0 }} />
                                  <span style={{ flex: 1, fontSize: 12 }}>{d.title}</span>
                                  <button
                                    type="button"
                                    data-testid={`open-proposal-doc-${d.id}`}
                                    onClick={() => openProposalDocument(d.id)}
                                    style={btnGhost()}
                                    title="Opens a 5-minute presigned link in a new tab"
                                  >
                                    <Eye size={12} /> View
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* ─── Send record + revoke ──────────────────────
                              This is the part that survives a reload. The
                              cleartext link does not; "who sent it and when"
                              does, and so does the ability to kill it. */}
                          {proposalTokens.length > 0 && (
                            <div style={{ marginTop: 12 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#6C757D", textTransform: "uppercase", marginBottom: 6 }}>
                                Sent to patient
                              </div>
                              {proposalTokens.map(t => {
                                const revoked = !!t.revoked_at;
                                const expired = !revoked && !!t.expires_at
                                  && new Date(t.expires_at).getTime() < Date.now();
                                return (
                                  <div
                                    key={t.id}
                                    data-testid={`proposal-token-${t.id}`}
                                    style={{
                                      padding: "8px 10px", marginBottom: 4, borderRadius: 6,
                                      background: revoked ? "#F1F5F9" : "white",
                                      border: `1px solid ${revoked ? "#CBD5E1" : "#E2E8F0"}`,
                                      opacity: revoked ? 0.75 : 1,
                                      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                    }}
                                  >
                                    <div style={{ flex: 1, minWidth: 180, fontSize: 11, color: "#475569" }}>
                                      <div style={{ color: "#0F172A", fontWeight: 600 }}>
                                        {t.created_by_name || "Unknown user"}
                                        {" · "}
                                        {new Date(t.created_at).toLocaleString()}
                                      </div>
                                      <div>
                                        {revoked ? (
                                          <span style={{ color: "#B91C1C" }}>
                                            Revoked {new Date(t.revoked_at).toLocaleDateString()}
                                            {t.revoked_by_name ? ` by ${t.revoked_by_name}` : ""}
                                            {t.revocation_reason ? ` — ${t.revocation_reason}` : ""}
                                          </span>
                                        ) : expired ? (
                                          <span style={{ color: "#B45309" }}>
                                            Expired {new Date(t.expires_at).toLocaleDateString()}
                                          </span>
                                        ) : (
                                          <>Active until {new Date(t.expires_at).toLocaleDateString()}</>
                                        )}
                                        {" · "}
                                        {t.use_count > 0
                                          ? `opened ${t.use_count}×`
                                          : "not opened yet"}
                                      </div>
                                    </div>
                                    {!revoked && canWriteProposal && (
                                      <button
                                        type="button"
                                        data-testid={`revoke-proposal-token-${t.id}`}
                                        onClick={() => revokeProposalToken(t.id, pr.id)}
                                        disabled={sendBusy}
                                        style={{ ...btnGhost(), color: "#DC3545" }}
                                        title="Immediately stops this link from opening the proposal"
                                      >
                                        <Ban size={12} /> Revoke
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Build new proposal button — only show when no current draft */}
            {!proposals.some(p => p.status === "draft" || p.status === "ready_for_patient") && canWrite && (
              <button onClick={createProposal} disabled={busy} style={btnPrimary()}>
                <Plus size={14} /> Build new proposal
              </button>
            )}
          </>
        )}
      </Card>

      <div style={{ textAlign: "right" }}>
        <button onClick={reload} disabled={busy} style={btnGhost()}>
          <RefreshCcw size={12} /> Refresh
        </button>
      </div>
    </div>
  );
}

// ─── Task #77: status color map ────────────────────────────────────────────
function proposalStatusColor(status) {
  return {
    draft: "#6C757D",
    ready_for_patient: "#2E8B98",
    patient_reviewing: "#7C3AED",
    selected: "#7C3AED",
    patient_signed: "#F59E0B",   // Phase 4 — awaiting physician counter-sign
    fully_signed: "#62BB46",     // Phase 4 — fully signed, ready for payment
    signed: "#2E8B98",
    deposit_paid: "#62BB46",
    paid_in_full: "#62BB46",
    expired: "#DC3545",
    cancelled: "#DC3545",
  }[status] || "#6C757D";
}

// ─── Tiny presentational helpers ────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div style={{
      background: "white", borderRadius: 12, padding: 16,
      boxShadow: "0 1px 3px rgba(0,0,0,.08)",
      border: "1px solid #E9ECEF",
    }}>
      <div style={{
        fontSize: 14, fontWeight: 700, color: "#212529",
        display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function ExclusionRow({ label, code, status, onChange, disabled, isAbsolute }) {
  const isUnscreened = status === "unscreened";
  return (
    <div style={{
      display: "flex", alignItems: "center", padding: "6px 8px",
      borderRadius: 6, marginBottom: 2,
      background: isUnscreened && isAbsolute ? "#FEF3C7" : "transparent",
    }}>
      <div style={{ flex: 1, fontSize: 12, color: "#212529" }}>
        {isAbsolute && (
          <span style={{
            background: "#DC3545", color: "white", fontSize: 9, padding: "1px 5px",
            borderRadius: 4, marginRight: 6, fontWeight: 700,
          }}>ABS</span>
        )}
        {label}
      </div>
      <select
        value={status}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        title={`Mark ${label} as screened`}
        style={{
          fontSize: 11, padding: "3px 6px", borderRadius: 4,
          border: `1px solid ${STATUS_COLOR[status] || "#DEE2E6"}`,
          background: "white", color: STATUS_COLOR[status] || "#212529",
          fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
          minWidth: 110,
        }}
      >
        <option value="unscreened">Unscreened</option>
        <option value="absent">Absent</option>
        <option value="present">Present</option>
        <option value="na">N/A</option>
      </select>
    </div>
  );
}

function sectionHeader() {
  return {
    fontSize: 12, fontWeight: 700, color: "#6C757D",
    textTransform: "uppercase", letterSpacing: 0.5,
    marginBottom: 6, display: "flex", alignItems: "center", gap: 6,
    paddingBottom: 4, borderBottom: "1px solid #F1F3F5",
  };
}
function fieldLabel() {
  return { display: "block", fontSize: 11, fontWeight: 600, color: "#6C757D", marginTop: 8, marginBottom: 3 };
}
function fieldInput() {
  return { width: "100%", padding: "6px 8px", fontSize: 12, borderRadius: 6, border: "1px solid #CED4DA" };
}
function fieldSelect() {
  return { ...fieldInput(), background: "white", cursor: "pointer" };
}
function fieldTextarea() {
  return { ...fieldInput(), fontFamily: "inherit", resize: "vertical" };
}
function btnPrimary() {
  return {
    background: "#2E8B98", color: "white", border: "none",
    padding: "6px 14px", borderRadius: 6, fontSize: 12, fontWeight: 600,
    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
  };
}
function btnDisabled() {
  return { ...btnPrimary(), background: "#CED4DA", color: "#6C757D", cursor: "not-allowed" };
}
function btnGhost() {
  return {
    background: "none", border: "1px solid #DEE2E6", color: "#6C757D",
    padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 4,
  };
}
